import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Heading from "../../components/ui/Heading";
import Tag from "../../components/ui/Tag";
import Button from "../../components/ui/Button";
import { useApi } from "../../context/ApiProvider";
import TextSkeleton from "../../components/skeletons/TextSkeleton";

const ProHome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");

  const [program, setProgram] = useState(null);
  const [dept, setDept] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return undefined;
    }
    let isMounted = true;
    setLoading(true);
    api
      .getProgramDetail(id)
      .then(async (result) => {
        if (!isMounted) return;
        const prog = result?.[0] || null;
        setProgram(prog);
        if (prog?.DeptID) {
          const deptResult = await api.getDeptAbout(prog.DeptID);
          if (isMounted) setDept(deptResult?.[0] || null);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [id, api]);

  return (
    <>
      <Header />

      <div className="mx-auto max-w-4xl px-4 py-12 sm:py-16 md:py-20">
        {loading ? (
          <TextSkeleton lines={6} />
        ) : !program ? (
          <div className="rounded-sm border border-dashed border-stone-300 bg-white p-10 text-center">
            <Heading level={4} className="mb-2 text-primary-800">
              Program not found
            </Heading>
          </div>
        ) : (
          <>
            {dept && (
              <button
                type="button"
                onClick={() => navigate(`/DptHome?id=${dept.DeptID}`)}
                className="mb-8 text-sm font-semibold text-primary-700 underline-offset-2 hover:underline"
              >
                ← Back to {dept.DeptTitle}
              </button>
            )}

            <div className="mb-6 flex flex-wrap items-center gap-2">
              <Tag color="secondary">{program.DEGREE_TYPE}</Tag>
              <Tag color="accent">{program.DURATION}</Tag>
            </div>

            <Heading level={3} className="mb-8 text-primary-900">
              {program.PGM_TITLE}
            </Heading>

            <p className="mb-10 text-lg leading-relaxed text-stone-700">{program.PROGRAM_DESC}</p>

            {(dept?.PEOS?.length > 0 || dept?.PLOS?.length > 0) && (
              <div className="mb-10 grid grid-cols-1 gap-10 sm:grid-cols-2">
                {dept.PEOS?.length > 0 && (
                  <div>
                    <Heading level={5} className="mb-4 text-primary-900">
                      Program Educational Objectives
                    </Heading>
                    <ul className="m-0 list-disc space-y-2 pl-5 text-sm leading-relaxed text-stone-600">
                      {dept.PEOS.map((peo, i) => (
                        <li key={i}>{peo.PEOS_TITLE}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {dept.PLOS?.length > 0 && (
                  <div>
                    <Heading level={5} className="mb-4 text-primary-900">
                      Program Learning Objectives
                    </Heading>
                    <ul className="m-0 list-disc space-y-2 pl-5 text-sm leading-relaxed text-stone-600">
                      {dept.PLOS.map((plo, i) => (
                        <li key={i}>{plo.PLOS_TITLE}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {dept && (
              <div className="mb-10">
                <Button variant="outline" onClick={() => navigate(`/CourseScheme?id=${dept.DeptID}`)}>
                  View Full Course Scheme →
                </Button>
              </div>
            )}

            {program.CAREER_PATHS?.length > 0 && (
              <div>
                <Heading level={5} className="mb-4 text-primary-900">
                  Career Prospects
                </Heading>
                <div className="flex flex-wrap gap-2.5">
                  {program.CAREER_PATHS.map((path) => (
                    <span
                      key={path}
                      className="rounded-sm bg-primary-50 px-3.5 py-2 text-sm font-medium text-primary-800"
                    >
                      {path}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </>
  );
};

export default ProHome;
